import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import moment from 'moment';
import Swal from 'sweetalert2';
import { IContaBancaria, IIndicadoresBloco, IIndicadoresVisao } from '../../interfaces/IModuloBancario';
import ModuloBancarioService from '../../services/ModuloBancarioService';

interface Props {
    esteiraId: number;
    /** "AAAA-MM"; vazio = todas. */
    competencia: string;
    contas: IContaBancaria[];
    versaoMinutas: number;
}

const EVIDENCIA: Record<string, string> = {
    COMPROVANTE: 'Comprovante',
    COBRANCA: 'Retorno de cobrança',
    RFB: 'Relatório RFB',
    NFSE: 'NFS-e',
    FOLHA: 'Folha analítica',
};

const mesAno = (c: string | null) => (c ? moment(c, 'YYYY-MM').format('MM/YYYY') : '—');
// O backend grava em UTC sem o "Z".
const dataBr = (d: string | null) => (d ? moment.utc(d).local().format('DD/MM/YYYY') : '—');
const percentual = (parte: number, total: number) =>
    (parte / total).toLocaleString('pt-BR', { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 });
const descreverConta = (c?: IContaBancaria) =>
    c ? `${c.banco} / ${c.agencia ? `${c.agencia} / ` : ''}${c.conta}${c.descricao ? ` — ${c.descricao}` : ''}` : 'Conta bancária removida';
const textoCobertura = (b: IIndicadoresBloco['cobertura'][number]) =>
    b.pedem === 0 ? 'Não se aplica' : `${b.comEvidencia} de ${b.pedem} (${percentual(b.comEvidencia, b.pedem)})`;

function Item({ rotulo, valor }: { rotulo: string; valor: number | string }) {
    return (
        <div className='d-flex justify-content-between gap-3 fs-7 py-1'>
            <span className='text-muted'>{rotulo}</span>
            <span className='fw-semibold text-nowrap'>{valor}</span>
        </div>
    );
}

function Assertividade({ dados }: { dados: IIndicadoresBloco['assertividade'] }) {
    if (!dados.aferida)
        return (
            <>
                <div className='fw-bold'>Assertividade não aferida</div>
                <div className='text-muted fs-7'>Nenhuma minuta autoaprovada foi revisada neste recorte. Autoaprovadas: {dados.autoaprovadas}.</div>
            </>
        );
    return (
        <>
            <div className='fw-bold'>Corretas: {dados.corretas} de {dados.revisadas} revisada(s) ({percentual(dados.corretas, dados.revisadas)})</div>
            <div className='text-muted fs-7'>
                Sobre as revisadas, sem projetar para as demais. Autoaprovadas vigentes: {dados.autoaprovadas}.
                Competências {mesAno(dados.competenciaInicio)} a {mesAno(dados.competenciaFim)}; revisões de {dataBr(dados.primeiraRevisao)} a {dataBr(dados.ultimaRevisao)}.
            </div>
        </>
    );
}

function Bloco({ dados }: { dados: IIndicadoresBloco }) {
    const { volume, aprovacao } = dados;
    return (
        <div className='row g-5'>
            <div className='col-12 col-md-6 col-xl-3'>
                <h5 className='fs-6 fw-bold mb-2'>Volume ({volume.total} linhas)</h5>
                <Item rotulo='Conciliadas com o razão' valor={volume.conciliadas} />
                <Item rotulo='Explicadas por minuta' valor={volume.explicadasPorMinuta} />
                <Item rotulo='Aguardando aprovação' valor={volume.aguardandoAprovacao} />
                <Item rotulo='Pendentes: conta' valor={volume.pendentesConta} />
                <Item rotulo='Pendentes: evidência' valor={volume.pendentesEvidencia} />
                <Item rotulo='Pendentes: saldo' valor={volume.pendentesSaldo} />
                <Item rotulo='Pendentes: divergência' valor={volume.pendentesDivergencia} />
                <Item rotulo='Reprovadas' valor={volume.reprovadas} />
                <Item rotulo='Sem minuta' valor={volume.semMinuta} />
            </div>
            <div className='col-12 col-md-6 col-xl-3'>
                <h5 className='fs-6 fw-bold mb-2'>Aprovação</h5>
                <Item rotulo='Humana' valor={aprovacao.humanas} />
                <Item rotulo='Automática: pontuação' valor={aprovacao.automaticasPorPontuacao} />
                <Item rotulo='Automática: tarifa' valor={aprovacao.automaticasPorTarifa} />
                <h5 className='fs-6 fw-bold mt-4 mb-2'>Pontuações</h5>
                {dados.pontuacoes.map(p => <Item key={p.faixa} rotulo={p.faixa} valor={p.quantidade} />)}
            </div>
            <div className='col-12 col-md-6 col-xl-3'>
                <h5 className='fs-6 fw-bold mb-2'>Cobertura de evidências</h5>
                <div className='text-muted fs-8 mb-1'>Só sobre as minutas que pedem cada tipo.</div>
                {dados.cobertura.map(c => (
                    <Fragment key={c.evidencia}>
                        <Item rotulo={EVIDENCIA[c.evidencia] ?? c.evidencia} valor={textoCobertura(c)} />
                        {c.dispensas.map(d => (
                            <div key={d.competencia} className='mb-1'>
                                <span className='badge badge-light-warning' title={d.justificativa}>Dispensado em {mesAno(d.competencia)}</span>
                            </div>
                        ))}
                    </Fragment>
                ))}
            </div>
            <div className='col-12 col-md-6 col-xl-3'>
                <h5 className='fs-6 fw-bold mb-2'>Assertividade auditada</h5>
                <Assertividade dados={dados.assertividade} />
            </div>
        </div>
    );
}

// DEVN3-778 — painel empresa → conta bancária → competência, calculado ao abrir a seção.
export default function IndicadoresBancarios({ esteiraId, competencia, contas, versaoMinutas }: Props) {
    const aberta = true;
    const [dados, setDados] = useState<IIndicadoresVisao | null>(null);
    const [carregando, setCarregando] = useState(false);
    const [abertas, setAbertas] = useState<Set<string>>(new Set());
    const ultimaBusca = useRef(0);

    const buscar = useCallback(async () => {
        const busca = ++ultimaBusca.current;
        setCarregando(true);
        try {
            const [ano, mes] = competencia ? competencia.split('-').map(Number) : [undefined, undefined];
            const resposta = await ModuloBancarioService.indicadores(esteiraId, ano, mes);
            if (busca === ultimaBusca.current) setDados(resposta);
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? 'Não foi possível carregar os indicadores.', icon: 'error' });
        } finally {
            if (busca === ultimaBusca.current) setCarregando(false);
        }
    }, [esteiraId, competencia]);

    useEffect(() => {
        if (aberta) buscar();
    }, [aberta, buscar, versaoMinutas]);

    const alternar = (chave: string) =>
        setAbertas(atual => {
            const novas = new Set(atual);
            if (!novas.delete(chave)) novas.add(chave);
            return novas;
        });

    return (
        <div className='card card-flush mb-4'>
            <div className='card-header align-items-center'>
                <h3 className='card-title fw-bold'>
                    Indicadores
                    {carregando && <span className='spinner-border spinner-border-sm ms-3'></span>}
                </h3>
            </div>
            {aberta && dados && (
                <div className='card-body pt-0'>
                    <div className='text-muted fs-7 mb-4'>
                        {competencia ? `Competência ${mesAno(competencia)}.` : 'Todas as competências da esteira.'} Cada linha do extrato conta uma vez, mesmo quando uma minuta explica várias.
                    </div>
                    <Bloco dados={dados.empresa} />

                    {dados.contas.length > 0 && <h4 className='fs-5 fw-bold mt-8 mb-2'>Por conta bancária</h4>}
                    {dados.contas.map(conta => {
                        const chave = `c${conta.contaBancariaId}`;
                        return (
                            <div key={chave} className='border-top py-3'>
                                <button type='button' className='btn btn-link p-0 fw-semibold text-start' onClick={() => alternar(chave)}>
                                    <i className={clsx('fas me-2', abertas.has(chave) ? 'fa-chevron-down' : 'fa-chevron-right')}></i>
                                    {descreverConta(contas.find(c => c.id === conta.contaBancariaId))}
                                </button>
                                {abertas.has(chave) && (
                                    <div className='mt-3 ms-md-5'>
                                        <Bloco dados={conta.indicadores} />
                                        {conta.competencias.map(c => {
                                            const chaveMes = `${chave}-${c.competencia}`;
                                            return (
                                                <div key={chaveMes} className='border-top mt-3 pt-3'>
                                                    <button type='button' className='btn btn-link p-0 fw-semibold text-start fs-7' onClick={() => alternar(chaveMes)}>
                                                        <i className={clsx('fas me-2', abertas.has(chaveMes) ? 'fa-chevron-down' : 'fa-chevron-right')}></i>
                                                        {mesAno(c.competencia)}
                                                    </button>
                                                    {abertas.has(chaveMes) && <div className='mt-3'><Bloco dados={c.indicadores} /></div>}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
